import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { createPageUrl } from "../../lib/utils";
import { ArrowRight, Sparkles, Star, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";

export default function HeroSection() {
  return (
    <div className="
      relative overflow-hidden
      bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700
      dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950
      transition-colors duration-300
    ">
      {/* Background glows */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-pink-400 dark:bg-red-600/30 rounded-full blur-3xl opacity-20 dark:opacity-30 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 dark:bg-orange-500/30 rounded-full blur-3xl opacity-20 dark:opacity-30 animate-pulse delay-700" />
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-purple-400 dark:bg-red-500/20 rounded-full blur-3xl opacity-20 dark:opacity-30 animate-pulse delay-1000" />
      </div>

      {/* Floating shapes */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-32 right-20 hidden lg:block"
      >
        <div className="w-20 h-20 bg-yellow-300 dark:bg-amber-500/40 rounded-2xl opacity-30 dark:opacity-40" />
      </motion.div>

      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-40 left-20 hidden lg:block"
      >
        <div className="w-16 h-16 bg-blue-300 dark:bg-red-500/30 rounded-full opacity-30 dark:opacity-40" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="flex items-center gap-2 mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Sparkles className="w-5 h-5 text-yellow-300 dark:text-red-400 animate-pulse" />
              <span className="text-blue-100 dark:text-red-200 font-semibold text-sm uppercase tracking-wide">
                Transforming Education
              </span>
            </motion.div>

            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white dark:text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Peer Tutoring
              <span className="
                block mt-2 bg-clip-text text-transparent
                bg-gradient-to-r from-yellow-300 via-pink-300 to-orange-400
                dark:from-red-400 dark:via-orange-400 dark:to-amber-400
              ">
                That Works
              </span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-blue-50 dark:text-zinc-300 mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Learn from <span className="font-bold text-white">elite students</span>{" "}
              who've conquered the same challenges you face.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Link to={createPageUrl("Bookings")}>
                <Button
                  size="lg"
                  className="
                    bg-gradient-to-r from-yellow-400 to-orange-500
                    dark:from-red-500 dark:to-orange-600
                    hover:from-yellow-500 hover:to-orange-600
                    dark:hover:from-red-600 dark:hover:to-orange-700
                    text-gray-900 dark:text-white
                    shadow-2xl group font-bold text-lg px-8 py-6 rounded-xl
                  "
                >
                  Start Learning Now
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>

              <Link to={createPageUrl("Tutors")}>
                <Button
                  size="lg"
                  variant="outline"
                  className="
                    border-3 border-white bg-white/10 backdrop-blur-sm text-white
                    dark:border-red-500/40 dark:bg-zinc-900/50
                    dark:hover:bg-zinc-800/70
                    font-semibold text-lg px-8 py-6 rounded-xl
                  "
                >
                  Meet Our Tutors
                </Button>
              </Link>
            </motion.div>

<motion.div
              className="grid grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              {[
                { value: "$65", label: "Per Hour", color: "text-yellow-300 dark:text-red-400" },
                { value: "100%", label: "Personalized", color: "text-green-300 dark:text-orange-400" },
                { value: "Elite", label: "Top Tutors", color: "text-white" }
              ].map((item, i) => (
                <div
                  key={i}
                  className="
                    bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20
                    dark:bg-zinc-900/70 dark:border-red-500/20
                  "
                >
                  <div className={`text-4xl font-bold mb-1 ${item.color}`}>
                    {item.value}
                  </div>
                  <div className="text-blue-100 dark:text-zinc-400 text-sm">
                    {item.label}
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              className="mt-6 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 dark:from-red-500/20 dark:to-orange-600/20 backdrop-blur-lg rounded-2xl p-5 border border-yellow-300/30 dark:border-red-400/30"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-yellow-300 dark:text-red-400 animate-pulse" />
                <div>
                  <div className="text-white font-bold text-lg">
                    🎉 SIBLING DISCOUNT!
                  </div>
                  <div className="text-blue-50 dark:text-zinc-300 text-sm mt-1">
                    Enroll first child for $65/hr • Additional siblings 10% off!
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

{/* Right Card */}
<motion.div
  className="hidden lg:block relative"
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.45, duration: 0.9, ease: "easeOut" }}
>
  {/* Ambient glow (tighter) */}
  <div className="
    absolute -inset-3 rounded-3xl blur-2xl
    bg-gradient-to-br from-indigo-400/30 via-purple-400/30 to-blue-400/30
    dark:from-red-500/25 dark:via-orange-500/25 dark:to-amber-500/25
  " />

  <div className="
    relative bg-white/10 backdrop-blur-2xl rounded-3xl p-10
    border border-white/20
    shadow-[0_30px_90px_rgba(0,0,0,0.35)]
    dark:bg-zinc-900/80 dark:border-red-500/20
  ">
    {/* Header */}
    <div className="mb-10">
      <h3 className="
        text-3xl font-extrabold
        bg-clip-text text-transparent
        bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300
        dark:from-red-300 dark:via-orange-300 dark:to-amber-300
      ">
        Areas of Focus
      </h3>
      <p className="text-amber-200/90 dark:text-zinc-400 mt-1">
        Subjects we specialize in
      </p>
    </div>

    {/* Focus list */}
    <div className="space-y-5">
      {[
        {
          label: "Mathematics",
          sub: "Algebra → Calculus → Beyond",
          accent: "bg-blue-500 dark:bg-blue-400",
          subColor: "text-blue-700/90 dark:text-blue-200/70",
          bgColor: "bg-blue-50/90 dark:bg-white/10"
        },
        {
          label: "Science",
          sub: "Physics · Chemistry · Biology",
          accent: "bg-purple-500 dark:bg-purple-400",
          subColor: "text-purple-700/90 dark:text-purple-200/70",
          bgColor: "bg-purple-50/90 dark:bg-white/10"
        },
        {
          label: "AP Coursework",
          sub: "STEM-focused AP classes",
          accent: "bg-amber-500 dark:bg-amber-400",
          subColor: "text-amber-700/90 dark:text-amber-200/70",
          bgColor: "bg-amber-50/90 dark:bg-white/10"
        },
        {
          label: "Advanced STEM",
          sub: "College-level concepts",
          accent: "bg-emerald-500 dark:bg-emerald-400",
          subColor: "text-emerald-700/90 dark:text-emerald-200/70",
          bgColor: "bg-emerald-50/90 dark:bg-white/10"
        },
      ].map(({ label, sub, accent, subColor, bgColor }) => (
        <div
          key={label}
          className={`
            flex items-center gap-4 rounded-2xl px-5 py-4
            ${bgColor} border border-blue-100
            dark:bg-zinc-800/60 dark:border-red-500/20
          `}
        >
          <div className={`w-3 h-3 rounded-full ${accent} dark:opacity-80`} />
          <div>
            <div className="text-lg font-semibold text-gray-700 dark:text-white">
              {label}
            </div>
            <div className={`text-sm ${subColor}`}>
              {sub}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</motion.div>

        </div>
      </div>
    </div>
  );
}